/* eslint-disable i18next/no-literal-string */

import { Button, Icon, OsStack, Paper, Typography } from "@outschool/backpack";
import { borderRadius } from "@outschool/backpack/dist-cjs/theme/borderRadius";
import { faChevronLeft, faSparkles } from "@outschool/icons";
import { HeroLayout } from "@outschool/ui-components-classroom";
import { Loading, LoadingIcon } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React, { useState } from "react";

import Footer from "../../layout/nav/Footer";
import Header from "../../layout/nav/Header";
import useAnalyticsPage, {
  PageType,
} from "../../lib/analytics/useAnalyticsPage";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import AnimalSelectionGrid from "./AnimalSelectionGrid";
import AvatarResult from "./AvatarResult";
import { useGenerateAvatarMutation } from "./hooks";

const MAX_SELECTED_ANIMALS = 3;
const MIN_SELECTED_ANIMALS = 2;

export default function AvatarBuilder() {
  // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
  useAnalyticsPage({ pageName: PageType.AvatarBuilder });
  const currentLearner = useCurrentLearner();
  const [selectedAnimals, setSelectedAnimals] = useState<string[]>([]);
  const [generatedAvatar, setGeneratedAvatar] = useState<{
    name: string;
    description: string;
    imageUrl: string;
  } | null>(null);
  const isMobile = useIsMobile();
  const [generateAvatar, { loading: isGenerating, error: generateError }] =
    useGenerateAvatarMutation();
  const handleAnimalToggle = (animalName: string) => {
    setSelectedAnimals(prev => {
      if (prev.includes(animalName)) {
        return prev.filter(name => name !== animalName);
      }
      if (prev.length < MAX_SELECTED_ANIMALS) {
        return [...prev, animalName];
      }
      return prev;
    });
  };

  const handleGenerate = async () => {
    if (selectedAnimals.length < MIN_SELECTED_ANIMALS) {
      return;
    }

    try {
      const result = await generateAvatar({
        variables: { animalNames: selectedAnimals },
      });

      if (result.data?.generateAvatar) {
        console.log("Generated avatar:", result.data.generateAvatar);
        setGeneratedAvatar({
          name: result.data.generateAvatar.name,
          description: result.data.generateAvatar.description,
          imageUrl: result.data.generateAvatar.imageUrl,
        });
      }
    } catch (error) {
      // Error is handled by the error state from the mutation
      console.error("Failed to generate avatar:", error);
    }
  };

  if (!currentLearner) {
    return <Loading />;
  }

  // Once avatar is generated, show only the result page (not the builder)
  if (generatedAvatar) {
    return (
      <HeroLayout header={<Header includeBlob />} footer={<Footer />}>
        <AvatarResult
          avatar={generatedAvatar}
          animals={selectedAnimals}
          onBack={() => setGeneratedAvatar(null)}
        />
      </HeroLayout>
    );
  }

  // Show the builder interface for selecting animals and generating avatar
  return (
    <HeroLayout header={<Header includeBlob />} footer={<Footer />}>
      <OsStack spacing={4}>
        <Button
          startIcon={<Icon icon={faChevronLeft} />}
          size="small"
          sx={{ width: "fit-content" }}
        >
          Back
        </Button>
        <Paper
          variant="elevation"
          sx={{
            borderRadius: borderRadius.medium,
            padding: { xs: "16px", md: "32px" },
          }}
        >
          <OsStack
            spacing={{ xs: 4, md: 6 }}
            direction="column"
            alignItems={isMobile ? "center" : "flex-start"}
          >
            <OsStack spacing={{ xs: 2, md: 4 }}>
              <Typography variant="h1">Create Avatar</Typography>
              <Typography variant="body1">
                Create your own awesome profile creature. Mix animals and see
                what new friend appears.
                <br />
                Pick 2 or 3 animals to mix together.
              </Typography>
            </OsStack>
            {/* Animal Selection Grid - Separated */}

            <AnimalSelectionGrid
              selectedAnimals={selectedAnimals}
              onAnimalToggle={handleAnimalToggle}
            />

            {/* Mix Animals Button */}
            {generateError && (
              <Typography variant="body2" color="error">
                Failed to generate avatar. Please try again.
              </Typography>
            )}
            <Button
              startIcon={isGenerating ? <LoadingIcon /> : undefined}
              endIcon={!isGenerating ? <Icon icon={faSparkles} /> : undefined}
              variant="contained"
              color="primary"
              onClick={handleGenerate}
              disabled={
                selectedAnimals.length < MIN_SELECTED_ANIMALS || isGenerating
              }
            >
              {isGenerating ? "Creating magic..." : "Mix My Creature!"}
            </Button>
          </OsStack>
        </Paper>
      </OsStack>
    </HeroLayout>
  );
}
